import classNames from 'classnames'
import {
  CSSProperties,
  JSX,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import { Loader } from 'core-components/library/loader'
import { useWidget } from 'core-components/sitebuilder/hooks/useWidget'
import { FlexGridWrapperContext } from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import { IRenderCustomContentParams } from 'core-components/sitebuilder/layout/flexItem/models/IRenderCustomContent'
import { WidgetContent } from 'core-components/sitebuilder/layout/flexItem/widgetItem/Content'
import { IFlexItem } from 'core-components/sitebuilder/models/flexGrid/IFlexItem'
import { IPageWidget, IWidget } from 'core-components/sitebuilder/models/IWidget'
import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { selectEditLayoutMode } from 'core-components/sitebuilder/store/selectors/selectEditLayoutMode'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { WidgetHeader } from './widgetItem/Header'
import { getRemoteUrl } from './widgetItem/helpers/getRemoteUrl'

import styles from './WidgetItem.module.scss'

export interface IWidgetItemProps extends PropsWithChildren {
  widgetId: string
  pageWidget?: IPageWidget
  widgetParams?: { [key: string]: any }
  routeParams?: { [key: string]: string }
  isFlex?: boolean
  allowEdit: boolean
  allowPinOut: boolean
  pinOut: boolean
  className?: string
  previewMode?: boolean
  onClickEdit?: () => void
  onClickRemove?: () => void
  onChangeScale?: (newScale: number, i: string) => void
  onChangeCustomSettings: (customWidgetSettings: unknown) => void
  onToggleColumnWidth: (bool?: boolean) => void
  widgetWidth: string
  height?: string
  systemWidgetSettings?: IFlexItem['systemWidgetSettings']
  fullWidth: boolean
  onImportSettings: (item: IFlexItem) => void
  onExportSettings: () => void
  renderCustomWidget?: (data: IRenderCustomContentParams) => JSX.Element | null
}

export type TRenderType = 'content' | 'contentRemote' | 'iframe' | 'react' | 'fromLibrary'

export const WidgetItem = (props: IWidgetItemProps) => {
  const widget = useWidget(props.widgetId)
  const editLayoutMode = useSelector(selectEditLayoutMode)
  const user = useSelector(selectUser)
  const appId = useSelector(selectAppId)
  const ownerId = useSelector(selectAppOwnerId)
  const { MeasureController } = useContext(FlexGridWrapperContext)

  const [contentHeight, setContentHeight] = useState<number | string>('auto')
  const [openFullScreen, setOpenFullScreen] = useState<boolean>(false)
  const [forceShowHeader, setForceShowHeader] = useState<boolean>(false)
  const [newHeight, setNewHeight] = useState<boolean>(false)

  const metric = useMemo(() => {
    return new MeasureController(widget, { user })
  }, [widget, user])

  const renderType = useMemo<TRenderType | undefined>(() => {
    const p = { ...props.widgetParams }
    let renderType: TRenderType | undefined = undefined
    if (widget.type === 'Static' && widget.content) {
      renderType = 'content'
    }

    if (widget.type === 'RemoteStatic') {
      renderType = 'contentRemote'
    }

    if (widget.type === 'Iframe' && widget.src) {
      p['iframeSrc'] = getRemoteUrl({ src: widget.src, queryParams: p, user, ownerId, appId })
      if (p['iframeSrc']) {
        renderType = 'iframe'
      }
    }

    if (widget.type === 'React') {
      renderType = 'react'
    }

    if (widget.type === 'FromLibrary') {
      renderType = 'fromLibrary'
    }
    return renderType
  }, [appId, ownerId, props.widgetParams, user, widget])

  const widgetParams = useMemo(() => {
    const params = { ...props.widgetParams }

    if (widget.type === 'Iframe' && widget.src) {
      params['iframeSrc'] = getRemoteUrl({
        src: widget.src,
        queryParams: params,
        user,
        ownerId,
        appId,
      })
    }
    return params
  }, [appId, ownerId, props.widgetParams, user, widget])

  const showHeader = !widget.hideHeader || forceShowHeader || editLayoutMode
  const allowAutoHeight = (widget: IWidget) => {
    if (props.isFlex && props.height === 'auto') {
      return true
    }
    return widget.autoHeight && widget.originH === widget.h
  }

  const nextStyle: CSSProperties = {
    backgroundColor: widget.styleSettings?.backgroundColor || '',
    borderRadius: widget.styleSettings?.borderRadius || '',
    border: widget.styleSettings?.border || '',
    margin: widget.styleSettings?.margins || '',
  }

  const contentStyles = {
    padding: widget.styleSettings?.paddings || 0,
  }

  const isAllowAutoHeight = allowAutoHeight(widget)
  if (newHeight && isAllowAutoHeight && nextStyle && contentHeight && contentHeight !== 'auto') {
    nextStyle.height = contentHeight
  }

  const onResize = (value: number) => {
    if (value && allowAutoHeight(widget)) {
      setContentHeight(value)
      setNewHeight(true)
    }
  }

  const handleOpenFullScreen = () => {
    setOpenFullScreen(true)
  }
  const handleCloseFullScreen = () => {
    setOpenFullScreen(false)
    onCloseFullScreen()
  }

  const onCloseFullScreen = () => {
    const event = new Event('onCloseFullScreen')
    setTimeout(() => {
      window.dispatchEvent(event)
    }, 100)
  }

  useEffect(() => {
    metric?.stop && metric?.stop('initialization')

    return () => {
      metric?.clearFullMetric && metric?.clearFullMetric()
    }
  }, [metric, widget])

  const renderHeader = () => {
    return (
      <WidgetHeader
        widget={widget}
        pageWidget={props.pageWidget}
        color={widget.styleSettings?.headerColor}
        title={props.routeParams && props.routeParams.name ? props.routeParams.name : ''}
        allowEdit={props.allowEdit}
        allowPinOut={props.allowPinOut}
        pinOut={props.pinOut}
        onChangeScale={props.onChangeScale}
        onClickEdit={props.onClickEdit}
        onClickRemove={props.onClickRemove}
        onToggleColumnWidth={props.onToggleColumnWidth}
        onClickHideHeader={() => {
          setForceShowHeader(false)
        }}
        onImportSettings={props.onImportSettings}
        onExportSettings={props.onExportSettings}
        isFullScreen={openFullScreen}
        onCloseFullScreen={handleCloseFullScreen}
        onClickFullScreen={handleOpenFullScreen}
      />
    )
  }

  if (!renderType) {
    return (
      <div>
        {renderHeader()}
        <Loader />
      </div>
    )
  }

  return (
    <div
      className={classNames(
        'widget__item',
        styles.card,
        props.className || '',
        showHeader && styles.withHeader,
        openFullScreen ? styles.fullscreen : ''
      )}
      style={nextStyle}
    >
      {showHeader || props.allowPinOut || openFullScreen ? renderHeader() : null}

      {props.previewMode ? (
        <div className={styles.emtydescription}>{widget.description || 'Preview mode'}</div>
      ) : (
        <WidgetContent
          widget={widget}
          pageWidget={props.pageWidget}
          style={contentStyles}
          widgetParams={widgetParams}
          routeParams={props.routeParams}
          renderType={renderType}
          height={openFullScreen ? 'calc(100vh - 96px)' : contentHeight}
          onResize={onResize}
          allowAutoHeight={allowAutoHeight}
          onChangeCustomSettings={props.onChangeCustomSettings}
          onToggleColumnWidth={props.onToggleColumnWidth}
          widgetWidth={props.widgetWidth}
          metrics={metric}
          fullWidth={openFullScreen ? true : props.fullWidth}
          systemWidgetSettings={props.systemWidgetSettings}
          isFullScreen={openFullScreen}
          onCloseFullScreen={handleCloseFullScreen}
          onOpenFullScreen={handleOpenFullScreen}
          renderCustomWidget={props.renderCustomWidget}
        />
      )}

      {props.children}
    </div>
  )
}
